import { ChevronDown, ChevronUp, Check } from 'lucide-react';
import React, { useState, useEffect, useRef } from 'react';

import { CommandListChevron } from './command-list-chevron/component';
import { cn } from '@repo/ui/lib/utils';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../ui/command';

export type SearchInputStateChange = {
  closeDropdown: () => void;
  isDropdownOpen: boolean;
};
export type SearchInputItem = {
  label: string;
  value: string;
  labelTrailingText?: number;
};
export const SearchInput: React.FC<{
  emptyResultsText?: string;
  onChange?: (data: SearchInputStateChange) => void;
  items: SearchInputItem[];
  selectedItemValues?: string[];
  onSelectItem?: (data: SearchInputItem) => void;
}> = ({
  emptyResultsText,
  onChange,
  items,
  selectedItemValues = [],
  onSelectItem,
}) => {
  const selectingRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [scrollRatio, setScrollRatio] = useState(0);

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    handleOnChange(isOpen);
  };

  const handleOnChange = (isOpen: boolean) => {
    onChange?.({
      isDropdownOpen: isOpen,
      closeDropdown: () => handleOpenChange(false),
    });
  };

  const handleSelectItem = (item: SearchInputItem) => {
    selectingRef.current = true;
    onSelectItem?.(item);
    setScrollRatio(0);
    handleOpenChange(false);

    setTimeout(() => {
      selectingRef.current = false;
    }, 100);
  };

  const handleScrollChange = (
    event: React.UIEvent<HTMLDivElement, UIEvent>,
  ) => {
    const { scrollTop, scrollHeight, clientHeight } =
      event.target as HTMLElement;
    setScrollRatio(scrollTop / (scrollHeight - clientHeight));
  };

  useEffect(() => {
    handleOnChange(open);
  }, []);

  return (
    <Command className="overflow-visible relative">
      <CommandInput
        placeholder="Search"
        onBlur={() => {
          if (!selectingRef.current) {
            setTimeout(() => handleOpenChange(false), 400);
          }
        }}
        onFocus={() => handleOpenChange(true)}
        wrapperClassName="rounded-md border border-cn-border z-5"
      />
      {open && (
        <CommandList
          className="border border-cn-border rounded-md shadow-md absolute inset-x-0 top-[2.625rem] max-h-56 bg-cn-popover z-10"
          onScroll={handleScrollChange}
        >
          <CommandListChevron
            className="top-0 z-100"
            hidden={scrollRatio <= 0.1}
            onFilteredCountChange={() => setScrollRatio(0)}
          >
            <ChevronUp className="w-4 h-4" />
          </CommandListChevron>
          <CommandEmpty>{emptyResultsText}</CommandEmpty>
          <CommandGroup>
            {items.map((item) => (
              <CommandItem
                className="data-[selected=true]:bg-cn-accent hover:cursor-pointer"
                key={item.value}
                value={item.value}
                onSelect={() => handleSelectItem(item)}
              >
                <section className="flex flex-row justify-between items-center w-full font-normal">
                  <span className="flex flex-row text-sm">
                    <Check
                      className={cn('w-4 h-4 mx-2', {
                        'opacity-100': selectedItemValues.includes(item.value),
                        'opacity-0': !selectedItemValues.includes(item.value),
                      })}
                    />
                    {item.label}
                  </span>

                  {item.labelTrailingText && (
                    <span className="text-xs opacity-60">
                      {item.labelTrailingText}
                    </span>
                  )}
                </section>
              </CommandItem>
            ))}
          </CommandGroup>
          <CommandListChevron
            className="bottom-0"
            hidden={items.length <= 6 || scrollRatio >= 0.9}
            onFilteredCountChange={() => setScrollRatio(0)}
          >
            <ChevronDown className="w-4 h-4" />
          </CommandListChevron>
        </CommandList>
      )}
    </Command>
  );
};
